import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { V2AdminListPortfolioResearchResponses } from '@workspace/api-backend'
import { v2AdminListPortfolioResearchOptions } from '@workspace/api-backend/react-query'
import { Badge } from '@workspace/ui/components/badge'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import { Pagination } from '@workspace/ui/components/pagination'
import { useState } from 'react'

type SearchParams = {
  page?: number
  status?: string
}

export const Route = createFileRoute('/admin/investors/portfolio-research')({
  component: AdminPortfolioResearchPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    status: (search.status as string) || undefined,
  }),
})

type PortfolioResearchItem = V2AdminListPortfolioResearchResponses[200]['data']['items'][number]

const LIMIT = 50
const PORTFOLIO_RESEARCH_STATUSES = [
  'PENDING',
  'ENQUEUED',
  'ACTIVE',
  'COMPLETED',
  'FAILED',
  'CANCELED',
] as const
type PortfolioStatus = (typeof PORTFOLIO_RESEARCH_STATUSES)[number]

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'destructive'> = {
  PENDING: 'secondary',
  ENQUEUED: 'warning',
  ACTIVE: 'warning',
  COMPLETED: 'success',
  FAILED: 'destructive',
  CANCELED: 'secondary',
}

function formatDate(date: string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function AdminPortfolioResearchPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [selectedStatus, setSelectedStatus] = useState<PortfolioStatus | ''>(
    (search.status as PortfolioStatus) ?? ''
  )
  const currentPage = search.page ?? 1

  const { data: response, isLoading } = useQuery(
    v2AdminListPortfolioResearchOptions({
      query: { page: currentPage, status: selectedStatus || undefined },
    })
  )

  const items: PortfolioResearchItem[] = response?.data?.items ?? []
  const total: number = response?.data?.total ?? 0
  const totalPages = Math.ceil(total / LIMIT)

  const columns: TableColumn<PortfolioResearchItem>[] = [
    {
      header: 'Run ID',
      width: '120px',
      render: (item) => <span className="font-mono text-xs">{item.id.slice(0, 8)}</span>,
    },
    {
      header: 'Investor',
      render: (item) => (
        <Link
          to="/admin/investors/$id"
          params={{ id: item.investorId }}
          className="font-medium text-primary hover:underline"
        >
          {item.investorName ?? item.investorId.slice(0, 8)}
        </Link>
      ),
    },
    {
      header: 'Status',
      width: '120px',
      render: (item) => (
        <Badge variant={STATUS_VARIANT[item.status] ?? 'secondary'} size="sm">
          {item.status}
        </Badge>
      ),
    },
    {
      header: 'Founders Found',
      width: '140px',
      render: (item) => (
        <CellText>{item.foundersFound != null ? String(item.foundersFound) : '—'}</CellText>
      ),
    },
    {
      header: 'Error',
      render: (item) =>
        item.error ? (
          <span className="block max-w-md truncate text-xs text-destructive" title={item.error}>
            {item.error}
          </span>
        ) : (
          <CellText>—</CellText>
        ),
    },
    {
      header: 'Created',
      width: '160px',
      render: (item) => <CellText>{formatDate(item.createdAt.toString())}</CellText>,
    },
    {
      header: 'Updated',
      width: '160px',
      render: (item) => <CellText>{formatDate(item.updatedAt.toString())}</CellText>,
    },
  ]

  return (
    <div className="w-full space-y-4">
      <div className="flex gap-2 flex-wrap">
        {(['', ...PORTFOLIO_RESEARCH_STATUSES] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => {
              setSelectedStatus(s)
              void navigate({ search: { page: 1, status: s || undefined } })
            }}
            className={`rounded px-3 py-1 text-sm ${
              selectedStatus === s
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        emptyMessage="No portfolio research jobs found."
        keyExtractor={(item) => item.id}
      />

      <Pagination
        page={currentPage}
        totalPages={totalPages}
        onPageChange={(p) =>
          void navigate({ search: { page: p, status: selectedStatus || undefined } })
        }
      />
    </div>
  )
}
